import React, { useEffect, useState } from "react";
import InputField from "components/fields/InputField";
import Checkbox from "components/checkbox";
import Footer from "components/footer/FooterAuthDefault";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import routes from "routes.js";
import FixedPlugin from "components/fixedPlugin/FixedPlugin";
import { setItem, getItem } from "utils/localStorage";
import axiosInstance from "../../axiosConfig";
import { jwtDecode } from "jwt-decode";
import "utils/loadingSpinner.css";

function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false); // Fixed: Properly define loading state
  const navigate = useNavigate();

  const handleChangePassword = async () => {
    // Clear previous error
    setError("");
    
    // Validate passwords
    if (newPassword !== repeatPassword) {
      setError("New password and repeat password do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters long.");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.post("/change-password", {
        currentPassword,
        newPassword,
      });
      navigate("/admin/default");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to change password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-xl bg-white dark:bg-navy-800 shadow-lg rounded-xl p-8 md:p-12 z-20">
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
          <div className="loading-text">Loading...</div>
        </div>
      )}
      <h4 className="mb-4 text-4xl font-bold text-center text-navy-700 dark:text-white">
        Change Password
      </h4>
      <p className="mb-6 text-center text-base text-gray-600 dark:text-gray-400">
        Please update your password to continue.
      </p>

      {error && <p className="mb-4 text-red-500 text-center">{error}</p>}

      <InputField
        variant="auth"
        extra="mb-3"
        label="Current Password*"
        placeholder="Enter current password"
        id="currentPassword"
        type="password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
      />
      <InputField
        variant="auth"
        extra="mb-3"
        label="New Password*"
        placeholder="Min. 8 characters"
        id="newPassword"
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />
      <InputField
        variant="auth"
        extra="mb-3"
        label="Repeat New Password*"
        placeholder="Repeat new password"
        id="repeatPassword"
        type="password"
        value={repeatPassword}
        onChange={(e) => setRepeatPassword(e.target.value)}
      />

      <button
        onClick={handleChangePassword}
        className="w-full bg-brand-500 hover:bg-brand-600 active:bg-brand-700 text-white dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200 py-3 rounded-xl text-base font-medium transition duration-200"
        disabled={loading}
      >
        {loading ? "Changing..." : "Change Password"}
      </button>
    </div>
  );
}

export default function Auth() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignIn = async () => {
    // Clear previous error
    setError("");
    
    try {
      setLoading(true);
      const response = await axiosInstance.post("/login", { username, password });
      const { access_token } = response.data;
      const refreshTime = Date.now();
      setItem("access_token", access_token);
      setItem("refreshed_at", refreshTime);

      const decodedToken = jwtDecode(access_token);
      if (decodedToken.force_pwd_change) {
        setShowLogin(false);
        navigate("/auth/change-password");
      } else {
        navigate("/admin/default");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to sign in. Please check your credentials"
      );
    } finally {
      setLoading(false);
    }
  };

  const getAuthRoutes = (routes) =>
    routes
      .filter((prop) => prop.layout === "/auth")
      .map((prop, key) => (
        <Route path={`/${prop.path}`} element={prop.component} key={key} />
      ));

  useEffect(() => {
    const token = getItem("access_token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.force_pwd_change) {
          setShowLogin(false);
          navigate("/auth/change-password");
        } else {
          navigate("/admin/default");
        }
      } catch (err) {
        // Invalid token, stay on auth page
      }
    }
  }, [navigate]);

  return (
    <div className="relative h-full min-h-screen w-full bg-white dark:bg-navy-900">
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
          <div className="loading-text">Loading...</div>
        </div>
      )}
      {location.pathname === "/auth/change-password" && (
        <div className="absolute inset-0 bg-black bg-opacity-50 z-10"></div>
      )}
      <FixedPlugin />
      <main className="flex items-center justify-center min-h-screen px-4 relative z-20">
        <div className="w-full max-w-xl bg-white dark:bg-navy-800 shadow-lg rounded-xl p-8 md:p-12">
          <Routes>
            {getAuthRoutes(routes)}
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/" element={<Navigate to="/auth/sign-in" replace />} />
          </Routes>

          {showLogin && (
            <>
              <h4 className="mb-4 text-4xl font-bold text-center text-navy-700 dark:text-white">
                Sign In
              </h4>
              <p className="mb-6 text-center text-base text-gray-600 dark:text-gray-400">
                Enter your username and password to sign in!
              </p>

              {error && <p className="mb-4 text-red-500 text-center">{error}</p>}

              <InputField
                variant="auth"
                extra="mb-3"
                label="Email*"
                placeholder="mail@simmmple.com"
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <InputField
                variant="auth"
                extra="mb-3"
                label="Password*"
                placeholder="Min. 8 characters"
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center">
                  <Checkbox
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                  />
                  <p className="ml-2 text-sm font-medium text-navy-700 dark:text-white">
                    Keep me logged in
                  </p>
                </div>
                <a
                  className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
                  href="/auth/forgot-password"
                >
                  Forgot Password?
                </a>
              </div>

              <button
                onClick={handleSignIn}
                className="w-full bg-brand-500 hover:bg-brand-600 active:bg-brand-700 text-white dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200 py-3 rounded-xl text-base font-medium transition duration-200"
                disabled={loading}
              >
                {loading ? "Signing In..." : "Sign In"}
              </button>

              <div className="mt-4 text-center">
                <span className="text-sm font-medium text-navy-700 dark:text-gray-400">
                  Not registered yet?
                </span>
                <a
                  href="/auth/register"
                  className="ml-1 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
                >
                  Create an account
                </a>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}